import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Form, Button, Spinner } from 'react-bootstrap';
import Message from '../../../Components/Message';
import apiClient from '../../../AppClient';
import ExpensesTransactionsModal from './ExpensesModal';
import { CURRENCY } from '../../../constants/Constants';

const ExpensesTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [modalError, setModalError] = useState(null); // New state for modal-specific errors

  useEffect(() => {
    loadTransactions();
    fetchExpenses();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [searchTerm, transactions]);

  const fetchExpenses = async () => {
    try {
      const response = await apiClient.get('/expenses/');
      setExpenses(response.data);
      setError(null);
    } catch (error) {
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
      // Fallback data
      setExpenses([
        { id: 1, expense:{id: 1, name: 'مصروف 1'} ,transaction_date: '2021-09-01', amount: 1000, notes: 'ملاحظات' },
        { id: 2, expense:{id: 2, name: 'مصروف 2'} ,transaction_date: '2021-09-02', amount: 2000, notes: 'ملاحظات' },
      ]);
    }
  };

  const loadTransactions = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('transactions/expenses/');
      const data = response.data;
      setTransactions(Array.isArray(data) ? data : [data]);
      setError(null);
    } catch (error) {
      console.error('خطأ في تحميل المعاملات:', error);
      if (error.response) {
        setError(error.response.data.detail);
      } else {
        setError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const filterTransactions = () => {
    if (!searchTerm.trim()) {
      setFilteredTransactions(transactions);
      return;
    }

    const filtered = transactions.filter(transaction => {
      const expenseName = transaction.expense?.name.toLowerCase() || '';
      const paymentMethod = transaction.payment_method?.toLowerCase() || '';
      const transaction_date = transaction.transaction_date.toLowerCase() || '';
      const expenseNotes = transaction.notes?.toLowerCase() || '';
      const expenseAmount = transaction.amount.toString().toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      return (
        expenseName.includes(searchLower) ||
        paymentMethod.includes(searchLower) ||
        expenseAmount.includes(searchLower) ||
        transaction_date.includes(searchLower) ||
        expenseNotes.includes(searchLower)
      );
    });

    setFilteredTransactions(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const addPayment = async (paymentData) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/transactions/expenses/add/', paymentData);
      alert('تمت الإضافة بنجاح!');
      loadTransactions();
      setShowAddModal(false);
      setModalError(null); // Clear any modal errors on success
    } catch (error) {
      // Set modal-specific error instead of general error
      if (error.response) {
        setModalError(error.response.data.detail);
      } else {
        setModalError('حدث خطأ أثناء الاتصال بالسيرفر');
      }
      // Don't close the modal when there's an error
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (formData) => {
    // Process form data from the modal
    const formattedPayment = {
      ...formData,
      expense: parseInt(formData.expense, 10),
      amount: parseFloat(formData.amount)
    };

    addPayment(formattedPayment);
  };

  const refreshData = () => {
    loadTransactions();
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  // Reset modal error when closing
  const handleCloseModal = () => {
    setModalError(null);
    setShowAddModal(false);
  };

  return (
    <Container fluid className="rtl-container" dir="rtl">
      <div className="header-container bg-success text-white p-3 mb-4">
        <h1 className="h3">المعاملات المالية - المصروفات</h1>
      </div>

      {error && <Message variant="danger">{error}</Message>}

      <div className="content-container bg-white rounded shadow p-4 mb-4">
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <Form.Control
                type="text"
                placeholder="بحث في المعاملات..."
                value={searchTerm}
                onChange={handleSearchChange}
                dir="ltr"
                style={{ textAlign: 'right' }}
              />
            </Form.Group>
          </Col>
          <Col md={6} className="d-flex justify-content-end">
            <Button
              style={{ marginLeft: '5px' }}
              variant="outline-secondary"
              className="me-2"
              onClick={refreshData}
            >
              🔄 تحديث
            </Button>
            <Button
              variant="success"
              onClick={() => setShowAddModal(true)}
            >
              <i className="fas fa-plus"></i> إضافة دفعة
            </Button>
          </Col>
        </Row>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="success" />
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center py-5">
            <div className="empty-state">
              <span className="display-4">📄</span>
              <p className="mt-3">لم يتم العثور على معاملات</p>
            </div>
          </div>
        ) : (
          <Table responsive bordered hover>
            <thead className="bg-light">
              <tr>
                <th>ID</th>
                <th>المصروف</th>
                <th>المبلغ</th>
                <th>تاريخ الدفع</th>
                <th>ملاحظات</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map(transaction => (
                <tr key={transaction.id}>
                  <td>{transaction.id}</td>
                  <td>{transaction.expense?.name || 'غير معروف'}</td>
                  <td>{formatCurrency(transaction.amount)} {CURRENCY}</td>
                  <td>{transaction.transaction_date}</td>
                  <td>{transaction.notes}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <ExpensesTransactionsModal
        show={showAddModal}
        handleClose={handleCloseModal}
        expenses={expenses}
        handleSubmit={handleSubmit}
        mode="add"
        modalError={modalError} // Pass the modal error to the modal component
      />
    </Container>
  );
};

export default ExpensesTransactions;